'use client';

import clsx from 'clsx';
import type { CtaProps } from '@/types/cta';
import { Image } from '@/components/raven/Image';
import { SafeHtml } from '@headstartwp/core/react';
import { Button } from '@/components/raven/ui/Button';
import { useRef, type FC, useState, useEffect } from 'react';
import { useOverlayContext } from '@/context/OverlayContext';
import { GalleryCaption } from '@/components/raven/GalleryCaption';
import type { ContentGallerySlideProps } from '@/types/content-gallery';
import { Expand, ArrowLeft, ArrowRight } from '@themaven-net/raven-assets';
import { GalleryAttribution } from '@/components/raven/GalleryAttribution';

import styles from '../styles.module.css';

const GalleryCta: FC<{ ctaLink?: CtaProps }> = ({ ctaLink }) => {
	const { opensInNewTab, title, url } = ctaLink ?? {};

	if (!title || !url) {
		return null;
	}

	return (
		(title || url) && (
			<Button
				className={styles.ctaButton}
				href={url ?? ''}
				rel={opensInNewTab ? 'noopener noreferrer' : undefined}
				target={opensInNewTab ? '_blank' : '_self'}
				variation="primary"
			>
				{title || url}
			</Button>
		)
	);
};

export const ContentGallerySlide: FC<ContentGallerySlideProps> = ({
	ctaLink,
	description,
	galleryId,
	image,
	setSlideIdx,
	slideIdx,
	slides,
	title,
}) => {
	const { openVariant } = useOverlayContext();
	const [fadeLeft, setFadeLeft] = useState<boolean>(false);
	const [fadeRight, setFadeRight] = useState<boolean>(false);
	const thumbnailsRef = useRef<HTMLDivElement>(null);

	const handleThumbnailScroll = () => {
		const el = thumbnailsRef.current;
		if (!el) return;

		const { scrollLeft } = el;
		const maxScrollLeft = el.scrollWidth - el.clientWidth;
		const atStart = scrollLeft <= 1;

		setFadeRight(scrollLeft < maxScrollLeft);
		setFadeLeft(!atStart);
	};
	useEffect(() => {
		thumbnailsRef.current?.children[slideIdx]?.scrollIntoView({
			behavior: 'smooth',
			block: 'nearest',
			inline: 'nearest',
		});
		handleThumbnailScroll();
	}, [slideIdx]);

	if (!image || !slides || slides?.length === 0) {
		return null;
	}

	const handlePrevSlide = () => {
		setSlideIdx(Math.max(0, slideIdx - 1));
		handleThumbnailScroll();
	};
	const handleNextSlide = () => {
		setSlideIdx(Math.min(slides.length - 1, slideIdx + 1));
		handleThumbnailScroll();
	};

	const {
		alt,
		caption,
		height,
		source_name: sourceName,
		source_url: sourceUrl,
		url,
		width,
	} = image;

	const openModal = () => {
		setSlideIdx(slideIdx);
		openVariant(galleryId);

		// Prevents scrolling of content behind modal when modal is open.
		document.body.classList.add('no-scroll');
	};

	return (
		<div className={styles.slide}>
			<div className={styles.panelImage}>
				<div className={styles.panelImageContent}>
					<figure className={styles.imageWrapper}>
						<Image
							alt={alt ?? ''}
							className={styles.image}
							height={Number(height ?? 0)}
							sizes="450px"
							src={url}
							width={Number(width ?? 0)}
						/>
						<Button
							aria-label={`Expand Image ${slideIdx + 1}${alt ? `. ${alt}` : ''}`}
							className={clsx(styles.button, styles.expand)}
							onClick={openModal}
							variation="icon-large"
						>
							<Expand />
						</Button>
					</figure>
					<div className={styles.metadata}>
						<GalleryCaption caption={caption} className={styles.caption} />
						<GalleryAttribution
							className={styles.attribution}
							sourceName={sourceName}
							sourceUrl={sourceUrl}
						/>
					</div>
				</div>
				<div className={styles.controlBar}>
					<div className={styles.thumbnailFadeWrapper}>
						<div
							className={clsx(styles.thumbnails)}
							onScroll={handleThumbnailScroll}
							ref={thumbnailsRef}
						>
							{slides.map(({ image: thumbnailImage }, i) => {
								return (
									<Button
										aria-label={`Go to slide ${i + 1}`}
										className={styles.thumbnailWrapper}
										key={`content-gallery-${galleryId}-thumbnail-${thumbnailImage.id}`}
										onClick={() => setSlideIdx(i)}
									>
										<Image
											alt={thumbnailImage.alt ?? ''}
											className={clsx(styles.thumbnail, {
												[styles.activeThumbnail]: slideIdx === i,
											})}
											height={Number(thumbnailImage.height ?? 50)}
											sizes="75px"
											src={thumbnailImage.url}
											width={Number(thumbnailImage.width ?? 75)}
										/>
									</Button>
								);
							})}
							{fadeLeft && <div className={styles.fadeLeft} />}
							{fadeRight && <div className={styles.fadeRight} />}
						</div>
					</div>
					<div className={styles.controls}>
						<span className={styles.slideCounter}>
							{slideIdx + 1} / {slides.length}
						</span>
						<div className={styles.arrowsWrapper}>
							<Button
								aria-label="Previous slide"
								className={styles.arrows}
								disabled={slideIdx === 0}
								onClick={handlePrevSlide}
								variation="icon-large"
							>
								<ArrowLeft />
							</Button>
							<Button
								aria-label="Next slide"
								className={styles.arrows}
								disabled={slideIdx >= slides.length - 1}
								onClick={handleNextSlide}
								variation="icon-large"
							>
								<ArrowRight />
							</Button>
						</div>
					</div>
				</div>
			</div>
			<div className={styles.panelContent}>
				{title && <h2 className={styles.title}>{title}</h2>}
				{description && (
					<div className={styles.description}>
						<SafeHtml html={description} />
					</div>
				)}
				<GalleryCta ctaLink={ctaLink} />
			</div>
		</div>
	);
};
